import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, type IChartApi, type ISeriesApi, ColorType } from "lightweight-charts";
import { getChart, type ChartResult } from "../../services/pulseService";

const RANGES = ["1d", "5d", "1mo", "3mo", "6mo", "ytd", "1y", "5y"] as const;
type Range = (typeof RANGES)[number];

const POPULAR_SYMBOLS = [
  { symbol: "^GSPC", label: "S&P 500" },
  { symbol: "^DJI", label: "Dow Jones" },
  { symbol: "^IXIC", label: "Nasdaq" },
  { symbol: "AAPL", label: "Apple" },
  { symbol: "MSFT", label: "Microsoft" },
  { symbol: "NVDA", label: "Nvidia" },
  { symbol: "TSLA", label: "Tesla" },
  { symbol: "AMZN", label: "Amazon" },
  { symbol: "BTC-USD", label: "Bitcoin" },
  { symbol: "ETH-USD", label: "Ethereum" },
];

export function SymbolChart() {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const ema9Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const ema21Ref = useRef<ISeriesApi<"Line"> | null>(null);
  const sma50Ref = useRef<ISeriesApi<"Line"> | null>(null);

  const [symbol, setSymbol] = useState("^GSPC");
  const [range, setRange] = useState<Range>("1d");
  const [data, setData] = useState<ChartResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customSymbol, setCustomSymbol] = useState("");
  const [showEMA, setShowEMA] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getChart(symbol, range);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load chart");
    } finally {
      setLoading(false);
    }
  }, [symbol, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute % change over period
  const periodChange = useMemo(() => {
    if (!data || data.candles.length < 2) return null;
    const first = data.candles[0].open;
    const last = data.candles[data.candles.length - 1].close;
    if (first === 0) return null;
    return ((last - first) / first) * 100;
  }, [data]);

  // Compute current price
  const currentPrice = useMemo(() => {
    if (!data || data.candles.length === 0) return null;
    return data.candles[data.candles.length - 1].close;
  }, [data]);

  // Create chart
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#94a3b8",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(148, 163, 184, 0.06)" },
        horzLines: { color: "rgba(148, 163, 184, 0.06)" },
      },
      crosshair: {
        vertLine: { color: "rgba(6, 182, 212, 0.3)", width: 1, style: 2 },
        horzLine: { color: "rgba(6, 182, 212, 0.3)", width: 1, style: 2 },
      },
      timeScale: {
        borderColor: "rgba(148, 163, 184, 0.1)",
        timeVisible: range === "1d" || range === "5d",
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: "rgba(148, 163, 184, 0.1)",
      },
      handleScroll: { vertTouchDrag: false },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderUpColor: "#22c55e",
      borderDownColor: "#ef4444",
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
    });

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "",
    });
    volumeSeries.priceScale().applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
    });

    // EMA 9 (fast - cyan)
    const ema9Series = chart.addSeries(LineSeries, {
      color: "rgba(6, 182, 212, 0.8)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    // EMA 21 (medium - yellow)
    const ema21Series = chart.addSeries(LineSeries, {
      color: "rgba(250, 204, 21, 0.7)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    // SMA 50 (slow - magenta)
    const sma50Series = chart.addSeries(LineSeries, {
      color: "rgba(217, 70, 239, 0.6)",
      lineWidth: 1,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    chartRef.current = chart;
    seriesRef.current = candleSeries;
    volumeRef.current = volumeSeries;
    ema9Ref.current = ema9Series;
    ema21Ref.current = ema21Series;
    sma50Ref.current = sma50Series;

    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeRef.current = null;
      ema9Ref.current = null;
      ema21Ref.current = null;
      sma50Ref.current = null;
    };
  }, [range]);

  // Update data
  useEffect(() => {
    if (!data || !seriesRef.current || !volumeRef.current) return;

    const candles = data.candles.map((c) => ({
      time: (c.timestamp as number),
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));

    const volumes = data.candles.map((c) => ({
      time: (c.timestamp as number),
      value: c.volume,
      color: c.close >= c.open ? "rgba(34, 197, 94, 0.3)" : "rgba(239, 68, 68, 0.3)",
    }));

    seriesRef.current.setData(candles as any);
    volumeRef.current.setData(volumes as any);

    // Compute EMA/SMA overlays
    if (showEMA && ema9Ref.current && ema21Ref.current && sma50Ref.current) {
      const closes = data.candles.map((c) => c.close);
      const times = data.candles.map((c) => c.timestamp);

      const computeEMA = (values: number[], period: number) => {
        const result: { time: number; value: number }[] = [];
        const k = 2 / (period + 1);
        let ema = values[0];
        for (let i = 0; i < values.length; i++) {
          ema = i === 0 ? values[0] : values[i] * k + ema * (1 - k);
          if (i >= period - 1) {
            result.push({ time: times[i] as number, value: ema });
          }
        }
        return result;
      };

      const computeSMA = (values: number[], period: number) => {
        const result: { time: number; value: number }[] = [];
        for (let i = period - 1; i < values.length; i++) {
          let sum = 0;
          for (let j = i - period + 1; j <= i; j++) sum += values[j];
          result.push({ time: times[i] as number, value: sum / period });
        }
        return result;
      };

      ema9Ref.current.setData(computeEMA(closes, 9) as any);
      ema21Ref.current.setData(computeEMA(closes, 21) as any);
      sma50Ref.current.setData(closes.length >= 50 ? computeSMA(closes, 50) as any : []);
    } else if (ema9Ref.current && ema21Ref.current && sma50Ref.current) {
      ema9Ref.current.setData([]);
      ema21Ref.current.setData([]);
      sma50Ref.current.setData([]);
    }

    chartRef.current?.timeScale().fitContent();
  }, [data, showEMA]);

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (customSymbol.trim()) {
      setSymbol(customSymbol.trim().toUpperCase());
      setCustomSymbol("");
    }
  };

  const currentLabel = POPULAR_SYMBOLS.find((s) => s.symbol === symbol)?.label || symbol;

  return (
    <div className="glass-card p-4 sm:p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider">
            Chart
          </h3>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-lg font-bold text-surface-100">{currentLabel}</p>
            {currentPrice !== null && (
              <span className="text-sm text-surface-300 font-medium">
                ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
            {periodChange !== null && (
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                periodChange >= 0
                  ? "text-emerald-400 bg-emerald-500/10"
                  : "text-red-400 bg-red-500/10"
              }`}>
                {periodChange >= 0 ? "+" : ""}{periodChange.toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        {/* Custom symbol input */}
        <form onSubmit={handleCustomSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={customSymbol}
            onChange={(e) => setCustomSymbol(e.target.value)}
            placeholder="Symbol..."
            className="w-24 px-2.5 py-1.5 text-xs rounded-lg bg-surface-800/60 border border-surface-700/50 text-surface-200 placeholder:text-surface-600 focus:border-primary-500/50 focus:outline-none"
          />
          <button
            type="submit"
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-primary-500/20 border border-primary-500/30 text-primary-300 hover:bg-primary-500/30 transition-colors"
          >
            Go
          </button>
        </form>
      </div>

      {/* Symbol pills */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {POPULAR_SYMBOLS.map((s) => (
          <button
            key={s.symbol}
            onClick={() => setSymbol(s.symbol)}
            className={`px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors ${
              symbol === s.symbol
                ? "bg-primary-500/20 border border-primary-500/40 text-primary-300"
                : "bg-surface-800/40 border border-surface-700/30 text-surface-400 hover:text-surface-200 hover:border-surface-600/50"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Range selector + EMA toggle */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                range === r
                  ? "bg-accent-500/20 border border-accent-500/40 text-accent-300"
                  : "bg-surface-800/40 border border-surface-700/30 text-surface-500 hover:text-surface-300"
              }`}
            >
              {r.toUpperCase()}
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-surface-700/40" />

        {/* EMA/SMA toggle */}
        <button
          onClick={() => setShowEMA(!showEMA)}
          className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
            showEMA
              ? "bg-primary-500/20 border border-primary-500/40 text-primary-300"
              : "bg-surface-800/40 border border-surface-700/30 text-surface-500 hover:text-surface-300"
          }`}
        >
          EMA/SMA
        </button>

        {/* Legend when active */}
        {showEMA && (
          <div className="flex items-center gap-2 text-[10px]">
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-cyan-400 rounded inline-block" />
              <span className="text-surface-500">EMA9</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-yellow-400 rounded inline-block" />
              <span className="text-surface-500">EMA21</span>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-0.5 bg-purple-400 rounded inline-block" />
              <span className="text-surface-500">SMA50</span>
            </span>
          </div>
        )}
      </div>

      {/* Chart area */}
      <div className="relative rounded-lg overflow-hidden bg-surface-900/30 border border-surface-700/20">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-surface-900/60 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-surface-400 text-sm">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Loading...
            </div>
          </div>
        )}
        {error && !loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        <div ref={chartContainerRef} className="w-full h-[320px] sm:h-[400px]" />
      </div>
    </div>
  );
}
